<template>
  <!-- Mobile backdrop: dims the screen when the drawer is open; click closes it -->
  <div
    v-if="mobileOpen"
    class="nav-sidebar-backdrop"
    @click="emit('close')"
  ></div>

  <!-- Sidebar shell: width/visibility driven by isCollapsed (desktop collapse) and mobileOpen (mobile drawer) -->
  <aside class="nav-sidebar" :class="{ 'nav-sidebar--collapsed': isCollapsed, 'nav-sidebar--mobile-open': mobileOpen }">
    <!-- Brand / Logo -->
    <div class="nav-sidebar__brand">
      <!-- Clicking the logo/wordmark navigates home -->
      <div class="nav-sidebar__logo-wrap" @click="logoClick">
        <!-- Full wordmark logo: shown when expanded, or always inside the mobile drawer -->
        <LaunchPadLogo v-if="!isCollapsed || mobileOpen" :isDark="isDarkMode" class="nav-sidebar__logo-full" />
        <!-- <span v-else class="nav-sidebar__logo-letter">D</span> -->
        <!-- Collapsed state: icon-only brand mark, swapped light/dark via collapsedLogoSrc -->
        <span v-else class="nav-sidebar__logo-letter mt-1 ml-3">
          <img
            alt="Brand Logo"
            class="logo-image"
            :src="collapsedLogoSrc"
          />
        </span>
      </div>
      <!-- Mobile-only close button inside the drawer header -->
      <button v-show="mobileOpen" class="nav-sidebar__mobile-close" @click="emit('close')" aria-label="Close menu">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>
        </svg>
      </button>
      <!-- <button class="nav-sidebar__toggle" @click="toggleCollapse" :title="isCollapsed ? 'Expand menu' : 'Collapse menu'">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="5" width="18" height="2" rx="1" fill="currentColor"/>
          <rect x="3" y="11" width="18" height="2" rx="1" fill="currentColor"/>
          <rect x="3" y="17" width="18" height="2" rx="1" fill="currentColor"/>
        </svg>
      </button>-->
      <!-- Desktop collapse/expand toggle, pinned to the sidebar's right edge -->
      <button
        class="nav-sidebar__toggle arrow-box kt-btn-icon size-[30px] absolute end-0 translate-x-1/2 -translate-y-1/2 top-1/2" style="top: 38px !important;cursor:pointer"
        @click="toggleCollapse"
        :title="isCollapsed ? 'Expand menu' : 'Collapse menu'"
      >
        <!-- Right-pointing arrow: shown while collapsed; click expands the sidebar -->
        <svg v-if="isCollapsed" width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15 4V16" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
          <path d="M6 10H13" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
          <path d="M10 6L14 10L10 14" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        <!-- Left-pointing arrow: shown while expanded; click collapses the sidebar -->
        <svg v-else width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M5 4V16" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
        <path d="M14 10H7" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
          <path d="M10 6L6 10L10 14" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        <!-- <img v-if="isCollapsed" src="https://cdn.jsdelivr.net/npm/bootstrap-icons/icons/arrow-right.svg" />
        <img v-else src="https://cdn.jsdelivr.net/npm/bootstrap-icons/icons/arrow-left.svg" /> -->
      </button>
    </div>

<!-- Donate -->
    <div v-if="showDonateButton" class="nav-sidebar__donate-wrap">
      <!-- Donate CTA; shows a tooltip with the label when collapsed since the text label is hidden -->
      <button
        class="nav-sidebar__donate-btn"
        v-tooltip="isCollapsed ? { value: 'Donate', position: 'right' } : null"
        @click="handleDonate"
      >
        <!-- v-html: injects the inline donate SVG markup defined in the script -->
        <span class="nav-sidebar__donate-icon" v-html="donateIcon"></span>
        <span class="nav-sidebar__donate-label">Donate</span>
      </button>
    </div>

    <!-- Navigation -->
    <nav class="nav-sidebar__nav" role="navigation">
      <!-- Renders one entry per item in navItems; each item renders as one of the three variants below -->
      <template v-for="item in navItems" :key="item.id">
        <!-- Router-link for navigable items -->
        <router-link
          v-if="item.to"
          :to="item.to"
          class="nav-item"
          :class="{ 'nav-item--active': isItemActive(item) }"
          v-tooltip="isCollapsed ? { value: item.label, position: 'right' } : null"
          @click="handleNavClick(item)"
        >
          <!-- v-html: renders the item's inline SVG icon markup -->
          <span class="nav-item__icon" v-html="item.icon"></span>
          <span class="nav-item__label">{{ item.label }}</span>
          <!-- Unread/notification count badge, capped at "99+" -->
          <span v-if="item.badgeCount > 0" class="nav-item__badge">
            {{ item.badgeCount > 99 ? '99+' : item.badgeCount }}
          </span>
        </router-link>
        <!-- Clickable item with no route (e.g. Profile, Logout): runs item.action via handleNavClick -->
        <button
          v-else-if="item.action"
          class="nav-item"
          :class="{ 'nav-item--active': isItemActive(item) }"
          v-tooltip="isCollapsed ? { value: item.label, position: 'right' } : null"
          @click="handleNavClick(item)"
        >
          <!-- v-html: renders the item's inline SVG icon markup -->
          <span class="nav-item__icon" v-html="item.icon"></span>
          <span class="nav-item__label" style="text-align: left !important">{{ item.label }}</span>
        </button>
        <!-- Action-based items (no route) -->
        <!-- Fallback: item has neither a route nor an action, so it renders disabled/non-interactive -->
        <div
          v-else
          class="nav-item nav-item--disabled"
          v-tooltip="isCollapsed ? { value: item.label, position: 'right' } : null"
          aria-disabled="true"
        >
            <span class="nav-item__icon" v-html="item.icon"></span>
            <span class="nav-item__label">{{ item.label }}</span>
            <!-- Unread/notification count badge, capped at "99+" -->
            <span v-if="item.badgeCount > 0" class="nav-item__badge">
              {{ item.badgeCount > 99 ? '99+' : item.badgeCount }}
            </span>
          </div>
          <!--<div v-else class="nav-item"
          :class="{ 'nav-item--active': isItemActive(item), 'nav-item--logout': item.id === 'logout' }"
          :title="isCollapsed ? item.label : ''"
          @click="item.action && item.action()"
          role="button"
          tabindex="0"
          @keydown.enter="item.action && item.action()"
        >
          <span class="nav-item__icon" v-html="item.icon"></span>
          <span class="nav-item__label">{{ item.label }}</span>
        </div> -->
      </template>
    </nav>

    <!-- Fixed version footer -->
    <div class="nav-sidebar__version">
      <!-- <span v-if="!isCollapsed || mobileOpen">Version: {{ appVersion }}</span>
      <span v-else>{{ appVersion }}</span> -->
      <!-- Expanded/mobile: full "Version: x.x.x" label -->
      <span v-if="!isCollapsed || mobileOpen">Version: 1.0.17</span>
      <!-- Collapsed desktop: version number only, no room for the label -->
      <span v-else>1.0.17</span>
    </div>
  </aside>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import LaunchPadLogo from '@/components/Icons/DougJonesMainLogo.vue';
import { useThemeScheme } from '@/composables/useThemeSchema';
import { useBranding } from '@/composables/useBranding';
import eventBus from '@/helpers/eventBusHelper';
import { scrollToTop } from '@/helpers/scrollControlHelper';
import { logAnalyticsEvent } from '@/services/firebase';
/** Whether the notification-shake animation is currently playing (see triggerNotificationShake). */
const isShaking = ref(false);
/**
 * @emits toggle - Fired when the desktop collapse state changes, with the new isCollapsed boolean.
 * @emits nav-click - Fired whenever a nav item is clicked, with the clicked item object.
 * @emits close - Fired to request the parent close the mobile drawer.
 */
const emit = defineEmits(['toggle', 'nav-click', 'close']);

/**
 * @prop {Boolean} mobileOpen - Whether the mobile drawer is open; controlled by default.vue.
 */
// Mobile drawer prop — controlled by default.vue
const props = defineProps({
  mobileOpen: { type: Boolean, default: false },
});

const store = useStore();
const router = useRouter();
const route = useRoute();

const { isDarkMode } = useThemeScheme();
const { logo, themePreset } = useBranding();
/** Only the Doug Jones light brand preset shows the Donate CTA. */
const showDonateButton = computed(() => themePreset === 'dougjoneslight');

/** Collapsed-state brand mark image, swapped between dark/light variants from the branding config. */
const collapsedLogoSrc = computed(() => isDarkMode.value ? logo.iconDark : logo.icon);
/** Desktop sidebar collapsed/expanded state; toggled by toggleCollapse and by updateCollapse on resize. */
const isCollapsed = ref(false);
/** App version string from the build env; currently unused — the footer below renders a hardcoded version instead. */
const appVersion = import.meta.env.VITE_APP_VERSION;

/** Currently logged-in user, read from the Vuex store. */
const userdata = computed(() => store.getters.getUser);
// console.error("Leftbar userdata:", userdata.value)
/** Reserved flag for showing the full nav item set; not currently read elsewhere in this component. */
const isFullMenuItems = ref(false);
/** Local copy of the user's orgs, kept in sync with the store via the watch below. */
const localOrgs = ref([]);
const orgsLoading = ref(true); // true until first data arrives
/** User's organizations, sourced directly from the store. */
const myOrgs = computed(() => store.getters.getOnlyUserOrgs);
/** Tracks whether the viewport is at mobile width; updated on resize by updateIsMobile. */
const isMobile = ref(window.innerWidth < 1023);
/** Viewport height minus a 100px offset; not currently referenced in the template. */
const isMobileHeight = ref(window.innerHeight - 100);
// console.error("user name", myOrgs)
/** Count shown on the Messages nav item's badge. */
const unreadMessagesCount = computed(() => store.getters.getUnreadMessagesCount);

// Invite menu item is hidden when the admin has turned off invite codes —
// show_invite_code is the sole visibility switch (same /api/settings/get
// flag gating the Invite Code field on SignUpForm.vue; referral_code_mandatory
// only matters there, for required vs optional, once shown). Defaults to
// true so the item isn't yanked away after first render if the settings
// call is slow — it only disappears once we know it should.
const showInviteCode = ref(true);
const fetchShowInviteCode = async () => {
  try {
    const response = await fetch('/admin-api/api/settings/get');
    const data = await response.json();
    showInviteCode.value = data?.response?.show_invite_code ?? true;
  } catch (e) {
    showInviteCode.value = true;
  }
};
/** Flips the desktop collapsed/expanded state and notifies the parent via the `toggle` emit. */
const toggleCollapse = () => {
  isCollapsed.value = !isCollapsed.value;
  emit('toggle', isCollapsed.value);
};

/** Navigates to the home route when the logo is clicked. */
const logoClick = () => router.push('/');
/** Inline SVG markup for the donate button icon, injected via v-html. */
const donateIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
<path d="M9.0639 16.8982C9.0639 17.2213 9.32681 17.4842 9.64984 17.4842C9.97287 17.4842 10.2358 17.2213 10.2358 16.8982C10.2358 16.575 9.97287 16.3123 9.64984 16.3123C8.6806 16.3123 7.89203 15.5237 7.89203 14.5545C7.89203 13.7882 8.38489 13.1354 9.07016 12.8952V12.0391C9.07016 11.7154 9.33246 11.4531 9.6561 11.4531C9.97974 11.4531 10.242 11.7154 10.242 12.0391V12.8995C10.9207 13.1432 11.4077 13.7929 11.4077 14.5545C11.4077 14.8781 11.1454 15.1404 10.8217 15.1404C10.4981 15.1404 10.2358 14.8781 10.2358 14.5545C10.2358 14.2313 9.97287 13.9685 9.64984 13.9685C9.32681 13.9685 9.0639 14.2313 9.0639 14.5545C9.0639 14.8775 9.32681 15.1404 9.64984 15.1404C10.6191 15.1404 11.4077 15.929 11.4077 16.8982C11.4077 17.6598 10.9207 18.3095 10.242 18.553V19.0703C10.242 19.394 9.97974 19.6562 9.6561 19.6562C9.33246 19.6562 9.07016 19.394 9.07016 19.0703V18.5573C8.38489 18.3171 7.89203 17.6644 7.89203 16.8982C7.89203 16.5746 8.15433 16.3123 8.47797 16.3123C8.80161 16.3123 9.0639 16.5746 9.0639 16.8982Z" fill="currentColor"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M9.65625 22C7.28168 22 5.60764 21.6959 4.38831 21.0431C2.80353 20.1946 2 18.7424 2 16.7266C2 13.8085 3.65222 11.1136 6.00314 9.6947C5.72452 9.38358 5.55469 8.97296 5.55469 8.52344C5.55469 7.82321 5.96637 7.21744 6.56039 6.935L5.58353 3.93954C5.52539 3.76102 5.55621 3.56555 5.66638 3.41373C5.7767 3.26175 5.95294 3.17188 6.14062 3.17188H7.99884C8.24069 2.48981 8.89224 2 9.65625 2C10.4201 2 11.0714 2.48981 11.3134 3.17188H13.1719C13.3596 3.17188 13.5358 3.26175 13.6461 3.41373C13.7563 3.56555 13.7871 3.76102 13.729 3.93954L12.7521 6.935C13.3461 7.21744 13.7578 7.82321 13.7578 8.52344C13.7578 8.97266 13.5881 9.38297 13.31 9.69394C14.0692 10.152 14.7583 10.7492 15.3464 11.4615C15.4021 11.456 15.4586 11.4531 15.5156 11.4531H20.2422C21.2114 11.4531 22 12.2417 22 13.2109C22 13.6608 21.83 14.0715 21.5509 14.3828C21.8299 14.6941 22 15.1049 22 15.5547C22 16.0045 21.83 16.4153 21.5509 16.7266C21.8299 17.0378 22 17.4486 22 17.8984C22 18.3483 21.83 18.759 21.5509 19.0703C21.8299 19.3816 22 19.7924 22 20.2422C22 21.2114 21.2114 22 20.2422 22H15.5156C14.9773 22 14.495 21.7565 14.1722 21.3741C13.0425 21.7998 11.5883 22 9.65625 22ZM20.2422 16.1406C20.5652 16.1406 20.8281 15.8777 20.8281 15.5547C20.8281 15.2317 20.5652 14.9688 20.2422 14.9688H15.5156C15.1926 14.9688 14.9297 15.2317 14.9297 15.5547C14.9297 15.8777 15.1926 16.1406 15.5156 16.1406H20.2422ZM20.2422 18.4844C20.5652 18.4844 20.8281 18.2215 20.8281 17.8984C20.8281 17.5754 20.5652 17.3125 20.2422 17.3125H15.5156C15.1926 17.3125 14.9297 17.5754 14.9297 17.8984C14.9297 18.2215 15.1926 18.4844 15.5156 18.4844H20.2422ZM15.5156 20.8281H20.2422C20.5652 20.8281 20.8281 20.5652 20.8281 20.2422C20.8281 19.9192 20.5652 19.6562 20.2422 19.6562H15.5156C15.1926 19.6562 14.9297 19.9192 14.9297 20.2422C14.9297 20.5652 15.1926 20.8281 15.5156 20.8281ZM20.2422 13.7969C20.5652 13.7969 20.8281 13.534 20.8281 13.2109C20.8281 12.8879 20.5652 12.625 20.2422 12.625H15.5156C15.1926 12.625 14.9297 12.8879 14.9297 13.2109C14.9297 13.534 15.1926 13.7969 15.5156 13.7969H20.2422ZM7.32944 9.10938H11.9832C11.9883 9.10922 11.9933 9.10938 11.9982 9.10938H12C12.323 9.10938 12.5859 8.84647 12.5859 8.52344C12.5859 8.20102 12.3241 7.93857 12.0018 7.93765H12.0002L11.9959 7.9375H7.31647L7.31235 7.93765H7.31067C6.9884 7.93857 6.72656 8.20102 6.72656 8.52344C6.72656 8.84647 6.98947 9.10938 7.3125 9.10938H7.31387L7.31906 9.10934C7.32252 9.10931 7.32598 9.10927 7.32944 9.10938ZM9.07031 3.75781C9.07031 4.08145 8.80801 4.34375 8.48438 4.34375H6.94797L7.73776 6.76562H11.5747L12.3645 4.34375H11.4141V4.92969C11.4141 5.25333 11.1518 5.51562 10.8281 5.51562C10.5045 5.51562 10.2422 5.25333 10.2422 4.92969V3.75781C10.2422 3.43478 9.97928 3.17188 9.65625 3.17188C9.33322 3.17188 9.07031 3.43478 9.07031 3.75781ZM11.8834 10.2812H7.42679C4.91626 11.3292 3.17188 13.9629 3.17188 16.7266C3.17188 18.3203 3.73416 19.3636 4.94144 20.0099C5.98392 20.5681 7.48218 20.8281 9.65625 20.8281C11.4473 20.8281 12.7675 20.6523 13.7587 20.278C13.7586 20.2741 13.7585 20.2702 13.7584 20.2662C13.7581 20.2582 13.7578 20.2502 13.7578 20.2422C13.7578 19.7924 13.9279 19.3816 14.2069 19.0703C13.9279 18.759 13.7578 18.3483 13.7578 17.8984C13.7578 17.4486 13.9279 17.0378 14.2069 16.7266C13.9279 16.4153 13.7578 16.0045 13.7578 15.5547C13.7578 15.1049 13.9279 14.6941 14.2069 14.3828C13.9279 14.0715 13.7578 13.6608 13.7578 13.2109C13.7578 12.7364 13.9472 12.3055 14.2539 11.9889C13.5794 11.2355 12.7707 10.6519 11.8834 10.2812Z" fill="currentcolor"/>
</svg>`;

/** Logs a donate-click analytics event and opens the external donation page in a new tab. */
const handleDonate = () => {
  // Device, geography, session, traffic source, and timestamp are all
  // captured automatically by GA4 for every event — this just names the
  // click itself and attaches which page it happened from. See
  // src/services/firebase.js for the analytics setup this calls into.
  logAnalyticsEvent('donate_click', {
    button_location: 'leftbar',
    page_path: route.path,
    page_title: document.title,
  });
  window.open('https://secure.actblue.com/donate/dj-change-network', '_blank', 'noopener,noreferrer');
};
/**
 * Click handler shared by all nav item variants: emits `nav-click`, closes the mobile
 * drawer if open, resets to the default tab and scrolls to top for the home item, and
 * runs the item's own `action` (if any) for action-based items like Profile/Logout.
 * @param {Object} item - The nav item that was clicked.
 */
const handleNavClick = (item) => {
  emit('nav-click', item);
  // Close mobile drawer after navigation
  if (props.mobileOpen) emit('close');
  if (item.id === 'home') {
    eventBus.emit('set-default-tab');
    scrollToTop();
  }
  if (item.action) {
    item.action();
  }
};
/** Updates isMobile based on the current window width; used as the resize handler. */
function updateIsMobile() {
  isMobile.value = window.innerWidth < 1023;
}
// console.log("Leftbar followedGroups:", followedGroups.value)
/** Initializes isMobile and subscribes to window resize to keep it in sync. */
onMounted(() => {
  updateIsMobile();
  window.addEventListener('resize', updateIsMobile);
  fetchShowInviteCode();
});
/** Cleans up the resize listener registered above. */
onUnmounted(() => {
  window.removeEventListener('resize', updateIsMobile);
});
/** Keeps localOrgs in sync whenever the store's user-orgs getter changes; runs immediately on setup too. */
watch(
  () => store.getters.getOnlyUserOrgs,
  (newVal) => {
    if (Array.isArray(newVal)) {
      localOrgs.value = newVal;
    }
  },
  { immediate: true }
);
// Trigger shake animation
/** Sets isShaking true, then clears it after 500ms, to play a one-off shake animation. */
const triggerNotificationShake = () => {
  isShaking.value = true;
  setTimeout(() => {
    isShaking.value = false;
  }, 500);
};

// const logOut = () => store.dispatch('logOut', {});
/** Dispatches the store's logOut action; wired up as the Logout nav item's action. */
const logOut = () => store.dispatch("logOut", {});

/**
 * Determines whether a nav item should render as active for the current route.
 * @param {Object} item - Nav item, checked against its `matchPaths`.
 * @returns {boolean} true if the current path equals '/' (for a root match path) or starts with one of the item's match paths.
 */
const isItemActive = (item) => {
  const path = route.path;
  if (!item.matchPaths) return false;
  return item.matchPaths.some(p => {
    if (p === '/') return path === '/';
    return path.startsWith(p);
  });
};

// SVG icons for nav items
// const communityIcon = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none">
//   <circle cx="12" cy="5" r="2.5" fill="currentColor" opacity="0.9"/>
//   <circle cx="4" cy="15" r="2.5" fill="currentColor" opacity="0.9"/>
//   <circle cx="20" cy="15" r="2.5" fill="currentColor" opacity="0.9"/>
//   <line x1="12" y1="7.5" x2="4" y2="12.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
//   <line x1="12" y1="7.5" x2="20" y2="12.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
//   <line x1="4" y1="15" x2="20" y2="15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
// </svg>`;
/** Inline SVG for the Community (home) nav item icon. */
const communityIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
  <path d="M6 4C4.9 4 4 4.9 4 6C4 7.1 4.9 8 6 8C7.1 8 8 7.1 8 6C8 4.9 7.1 4 6 4ZM6 16C4.9 16 4 16.9 4 18C4 19.1 4.9 20 6 20C7.1 20 8 19.1 8 18C8 16.9 7.1 16 6 16ZM18 8C19.1 8 20 7.1 20 6C20 4.9 19.1 4 18 4C16.9 4 16 4.9 16 6C16 7.1 16.9 8 18 8ZM18 16C16.9 16 16 16.9 16 18C16 19.1 16.9 20 18 20C19.1 20 20 19.1 20 18C20 16.9 19.1 16 18 16Z" fill="currentColor"/>
  <line x1="18.25" y1="9.25" x2="18.25" y2="14.75" stroke="currentColor" stroke-width="0.5" stroke-linecap="round"/>
  <line x1="9.25" y1="5.75" x2="14.75" y2="5.75" stroke="currentColor" stroke-width="0.5" stroke-linecap="round"/>
  <line x1="6.25" y1="9.25" x2="6.25" y2="14.75" stroke="currentColor" stroke-width="0.5" stroke-linecap="round"/>
  <line x1="9.25" y1="17.75" x2="14.75" y2="17.75" stroke="currentColor" stroke-width="0.5" stroke-linecap="round"/>
</svg>`;

// FIX: the filled (fill="currentColor") version previously here rendered
// hollow/near-invisible when active — .nav-item--active's icon override
// (below, ~line 679) forces `fill: transparent; stroke: var(--color-text-black-white)`
// on every path/circle/etc, which only makes sense for stroke-drawn icons
// like every other nav icon originally was. Using a stroke-based bubble
// here instead keeps it correctly visible (and theme-adaptive) in the
// active state, and tracks `currentColor` normally otherwise.
// Old: speech-bubble outline icon
// const messagesIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
//   <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
// </svg>`;
const messagesIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 20 20" fill="none">
  <g clip-path="url(#clip0_2824_20251)">
    <path d="M18.3346 1.66675L9.16797 10.8334" stroke="currentColor" stroke-width="2.66667" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M18.3346 1.66675L12.5013 18.3334L9.16797 10.8334L1.66797 7.50008L18.3346 1.66675Z" stroke="currentColor" stroke-width="2.66667" stroke-linecap="round" stroke-linejoin="round"/>
  </g>
  <defs>
    <clipPath id="clip0_2824_20251">
      <rect width="20" height="20" fill="white"/>
    </clipPath>
  </defs>
</svg>`;

// const groupsIcon = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none">
//   <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
//   <circle cx="9" cy="7" r="4" stroke="currentColor" stroke-width="1.8"/>
//   <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
// </svg>`;
/** Inline SVG for the Groups nav item icon. */
const groupsIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
  <path d="M14.3712 11.9169C14.7527 11.3729 15.0588 10.7001 15.2685 9.94418C15.4782 9.18823 15.5868 8.36682 15.5868 7.53567C15.5868 5.97389 15.2101 4.47608 14.5396 3.37173C13.8691 2.26739 12.9597 1.64697 12.0115 1.64697C11.0633 1.64697 10.1539 2.26739 9.48337 3.37173C8.81288 4.47608 8.43619 5.97389 8.43619 7.53567C8.43619 8.36682 8.54474 9.18823 8.75448 9.94418C8.96423 10.7001 9.27025 11.3729 9.65179 11.9169C8.65081 12.6634 7.80155 13.869 7.20556 15.3895C6.60957 16.91 6.29207 18.681 6.29102 20.4908C6.29102 20.8032 6.36635 21.1027 6.50045 21.3236C6.63455 21.5445 6.81643 21.6686 7.00607 21.6686C7.19572 21.6686 7.3776 21.5445 7.5117 21.3236C7.6458 21.1027 7.72113 20.8032 7.72113 20.4908C7.72113 18.6167 8.17315 16.8193 8.97775 15.4941C9.78235 14.1689 10.8736 13.4244 12.0115 13.4244C13.1494 13.4244 14.2406 14.1689 15.0452 15.4941C15.8498 16.8193 16.3018 18.6167 16.3018 20.4908C16.3018 20.8032 16.3772 21.1027 16.5113 21.3236C16.6454 21.5445 16.8273 21.6686 17.0169 21.6686C17.2065 21.6686 17.3884 21.5445 17.5225 21.3236C17.6566 21.1027 17.732 20.8032 17.732 20.4908C17.7309 18.681 17.4134 16.91 16.8174 15.3895C16.2214 13.869 15.3722 12.6634 14.3712 11.9169ZM12.0115 11.0689C11.5872 11.0689 11.1725 10.8617 10.8197 10.4734C10.4669 10.0852 10.192 9.53339 10.0296 8.88778C9.86724 8.24217 9.82476 7.53175 9.90753 6.84638C9.9903 6.161 10.1946 5.53144 10.4946 5.03731C10.7946 4.54318 11.1769 4.20667 11.593 4.07034C12.0091 3.93401 12.4404 4.00398 12.8324 4.2714C13.2244 4.53883 13.5594 4.99169 13.7951 5.57272C14.0309 6.15376 14.1567 6.83687 14.1567 7.53567C14.1567 8.47274 13.9307 9.37143 13.5284 10.034C13.1261 10.6966 12.5804 11.0689 12.0115 11.0689Z" fill="currentColor"/>
  <path d="M5.02468 11.4458C4.56707 10.597 4.26814 9.5485 4.16389 8.42645C4.05964 7.30439 4.15451 6.15663 4.43707 5.12131C4.71963 4.08599 5.17785 3.20724 5.75657 2.59084C6.33528 1.97444 7.00982 1.64667 7.699 1.64697C7.88865 1.64697 8.07053 1.77106 8.20463 1.99192C8.33873 2.21279 8.41406 2.51236 8.41406 2.82471C8.41406 3.13707 8.33873 3.43663 8.20463 3.6575C8.07053 3.87837 7.88865 4.00245 7.699 4.00245C7.13007 4.00245 6.58443 4.3747 6.18213 5.03731C5.77983 5.69992 5.55383 6.59861 5.55383 7.53567C5.55484 8.15427 5.65444 8.76159 5.84267 9.29689C6.03089 9.8322 6.30115 10.2767 6.62641 10.586C6.73243 10.6867 6.82098 10.8306 6.88358 11.0038C6.94618 11.177 6.98073 11.3738 6.98394 11.5753C6.98694 11.7753 6.95897 11.9732 6.90267 12.1504C6.84637 12.3276 6.7636 12.4782 6.66217 12.5882L6.38329 12.8944L6.29034 12.9768C5.42841 13.6502 4.70125 14.7152 4.19451 16.0464C3.68778 17.3776 3.42256 18.9196 3.4301 20.4908C3.4301 20.8032 3.35476 21.1027 3.22066 21.3236C3.08656 21.5445 2.90469 21.6686 2.71504 21.6686C2.5254 21.6686 2.34352 21.5445 2.20942 21.3236C2.07532 21.1027 1.99998 20.8032 1.99998 20.4908C1.99414 18.6835 2.26897 16.9037 2.79837 15.3206C3.32777 13.7375 4.09414 12.4036 5.02468 11.4458Z" fill="currentColor"/>
  <path d="M18.9753 11.4458C19.4329 10.597 19.7319 9.5485 19.8361 8.42645C19.9404 7.30439 19.8455 6.15663 19.5629 5.12131C19.2804 4.08599 18.8221 3.20724 18.2434 2.59084C17.6647 1.97444 16.9902 1.64667 16.301 1.64697C16.1114 1.64697 15.9295 1.77106 15.7954 1.99192C15.6613 2.21279 15.5859 2.51236 15.5859 2.82471C15.5859 3.13707 15.6613 3.43663 15.7954 3.6575C15.9295 3.87837 16.1114 4.00245 16.301 4.00245C16.8699 4.00245 17.4156 4.3747 17.8179 5.03731C18.2202 5.69992 18.4462 6.59861 18.4462 7.53567C18.4452 8.15427 18.3456 8.76159 18.1573 9.29689C17.9691 9.8322 17.6988 10.2767 17.3736 10.586C17.2676 10.6867 17.179 10.8306 17.1164 11.0038C17.0538 11.177 17.0193 11.3738 17.0161 11.5753C17.0131 11.7753 17.041 11.9732 17.0973 12.1504C17.1536 12.3276 17.2364 12.4782 17.3378 12.5882L17.6167 12.8944L17.7097 12.9768C18.5716 13.6502 19.2987 14.7152 19.8055 16.0464C20.3122 17.3776 20.5774 18.9196 20.5699 20.4908C20.5699 20.8032 20.6452 21.1027 20.7793 21.3236C20.9134 21.5445 21.0953 21.6686 21.285 21.6686C21.4746 21.6686 21.6565 21.5445 21.7906 21.3236C21.9247 21.1027 22 20.8032 22 20.4908C22.0059 18.6835 21.731 16.9037 21.2016 15.3206C20.6722 13.7375 19.9059 12.4036 18.9753 11.4458Z" fill="currentColor"/>
</svg>`;

// Old: 4-square stroke grid icon
// const quickLinksIcon = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none">
//   <rect x="3" y="3" width="7" height="7" rx="1.5" stroke="currentColor" stroke-width="1.8"/>
//   <rect x="14" y="3" width="7" height="7" rx="1.5" stroke="currentColor" stroke-width="1.8"/>
//   <rect x="3" y="14" width="7" height="7" rx="1.5" stroke="currentColor" stroke-width="1.8"/>
//   <rect x="14" y="14" width="7" height="7" rx="1.5" stroke="currentColor" stroke-width="1.8"/>
// </svg>`;

// Old: calendar icon — replaced with chain-link icon matching sidebar fill style
// const quickLinksIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
//   <path d="M19 3H18V2C18 1.73478 17.8946 1.48043 17.7071 1.29289C17.5196 1.10536 17.2652 1 17 1C16.7348 1 16.4804 1.10536 16.2929 1.29289C16.1054 1.48043 16 1.73478 16 2V3H8V2C8 1.73478 7.89464 1.48043 7.70711 1.29289C7.51957 1.10536 7.26522 1 7 1C6.73478 1 6.48043 1.10536 6.29289 1.29289C6.10536 1.48043 6 1.73478 6 2V3H5C4.20435 3 3.44129 3.31607 2.87868 3.87868C2.31607 4.44129 2 5.20435 2 6V20C2 20.7956 2.31607 21.5587 2.87868 22.1213C3.44129 22.6839 4.20435 23 5 23H19C19.7956 23 20.5587 22.6839 21.1213 22.1213C21.6839 21.5587 22 20.7956 22 20V6C22 5.20435 21.6839 4.44129 21.1213 3.87868C20.5587 3.31607 19.7956 3 19 3ZM20 20C20 20.2652 19.8946 20.5196 19.7071 20.7071C19.5196 20.8946 19.2652 21 19 21H5C4.73478 21 4.48043 20.8946 4.29289 20.7071C4.10536 20.5196 4 20.2652 4 20V11H20V20ZM20 9H4V6C4 5.73478 4.10536 5.48043 4.29289 5.29289C4.48043 5.10536 4.73478 5 5 5H6V6C6 6.26522 6.10536 6.51957 6.29289 6.70711C6.48043 6.89464 6.73478 7 7 7C7.26522 7 7.51957 6.89464 7.70711 6.70711C7.89464 6.51957 8 6.26522 8 6V5H16V6C16 6.26522 16.1054 6.51957 16.2929 6.70711C16.4804 6.89464 16.7348 7 17 7C17.2652 7 17.5196 6.89464 17.7071 6.70711C17.8946 6.51957 18 6.26522 18 6V5H19C19.2652 5 19.5196 5.10536 19.7071 5.29289C19.8946 5.48043 20 5.73478 20 6V9Z" fill="currentColor"/>
//   <path d="M12.5556 18.8315C12.3911 18.9414 12.1978 19 12 19C11.7348 19 11.4804 18.8946 11.2929 18.7071C11.1054 18.5196 11 18.2652 11 18C11 17.8022 11.0586 17.6089 11.1685 17.4444C11.2784 17.28 11.4346 17.1518 11.6173 17.0761C11.8 17.0004 12.0011 16.9806 12.1951 17.0192C12.3891 17.0578 12.5673 17.153 12.7071 17.2929C12.847 17.4327 12.9422 17.6109 12.9808 17.8049C13.0194 17.9989 12.9996 18.2 12.9239 18.3827C12.8482 18.5654 12.72 18.7216 12.5556 18.8315Z" fill="currentColor"/>
//   <path d="M17.5556 18.8315C17.3911 18.9414 17.1978 19 17 19C16.7348 19 16.4804 18.8946 16.2929 18.7071C16.1054 18.5196 16 18.2652 16 18C16 17.8022 16.0586 17.6089 16.1685 17.4444C16.2784 17.28 16.4346 17.1518 16.6173 17.0761C16.8 17.0004 17.0011 16.9806 17.1951 17.0192C17.3891 17.0578 17.5673 17.153 17.7071 17.2929C17.847 17.4327 17.9422 17.6109 17.9808 17.8049C18.0194 17.9989 17.9996 18.2 17.9239 18.3827C17.8482 18.5654 17.72 18.7216 17.5556 18.8315Z" fill="currentColor"/>
//   <path d="M17.5556 14.8315C17.3911 14.9414 17.1978 15 17 15C16.7348 15 16.4804 14.8946 16.2929 14.7071C16.1054 14.5196 16 14.2652 16 14C16 13.8022 16.0586 13.6089 16.1685 13.4444C16.2784 13.28 16.4346 13.1518 16.6173 13.0761C16.8 13.0004 17.0011 12.9806 17.1951 13.0192C17.3891 13.0578 17.5673 13.153 17.7071 13.2929C17.847 13.4327 17.9422 13.6109 17.9808 13.8049C18.0194 13.9989 17.9996 14.2 17.9239 14.3827C17.8482 14.5654 17.72 14.7216 17.5556 14.8315Z" fill="currentColor"/>
//   <path d="M12.5556 14.8315C12.3911 14.9414 12.1978 15 12 15C11.7348 15 11.4804 14.8946 11.2929 14.7071C11.1054 14.5196 11 14.2652 11 14C11 13.8022 11.0586 13.6089 11.1685 13.4444C11.2784 13.28 11.4346 13.1518 11.6173 13.0761C11.8 13.0004 12.0011 12.9806 12.1951 13.0192C12.3891 13.0578 12.5673 13.153 12.7071 13.2929C12.847 13.4327 12.9422 13.6109 12.9808 13.8049C13.0194 13.9989 12.9996 14.2 12.9239 14.3827C12.8482 14.5654 12.72 14.7216 12.5556 14.8315Z" fill="currentColor"/>
//   <path d="M7.55557 14.8315C7.39112 14.9414 7.19778 15 7 15C6.73478 15 6.48043 14.8946 6.29289 14.7071C6.10536 14.5196 6 14.2652 6 14C6 13.8022 6.05865 13.6089 6.16853 13.4444C6.27841 13.28 6.43459 13.1518 6.61732 13.0761C6.80004 13.0004 7.00111 12.9806 7.19509 13.0192C7.38907 13.0578 7.56725 13.153 7.70711 13.2929C7.84696 13.4327 7.9422 13.6109 7.98079 13.8049C8.01937 13.9989 7.99957 14.2 7.92388 14.3827C7.84819 14.5654 7.72002 14.7216 7.55557 14.8315Z" fill="currentColor"/>
//   <path d="M7.55557 18.8315C7.39112 18.9414 7.19778 19 7 19C6.73478 19 6.48043 18.8946 6.29289 18.7071C6.10536 18.5196 6 18.2652 6 18C6 17.8022 6.05865 17.6089 6.16853 17.4444C6.27841 17.28 6.43459 17.1518 6.61732 17.0761C6.80004 17.0004 7.00111 16.9806 7.19509 17.0192C7.38907 17.0578 7.56725 17.153 7.70711 17.2929C7.84696 17.4327 7.9422 17.6109 7.98079 17.8049C8.01937 17.9989 7.99957 18.2 7.92388 18.3827C7.84819 18.5654 7.72002 18.7216 7.55557 18.8315Z" fill="currentColor"/>
// </svg>`;

/** Inline SVG for the Quick Links nav item icon. */
const quickLinksIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
  <path d="M10.8077 16.5384H7.03848C5.78285 16.5384 4.71253 16.096 3.82753 15.2111C2.94253 14.3262 2.50003 13.256 2.50003 12.0005C2.50003 10.745 2.94253 9.67467 3.82753 8.78942C4.71253 7.90417 5.78285 7.46155 7.03848 7.46155H10.8077V8.9615H7.03848C6.19873 8.9615 5.48238 9.25797 4.88943 9.85092C4.29648 10.4439 4.00001 11.1602 4.00001 12C4.00001 12.8397 4.29648 13.5561 4.88943 14.149C5.48238 14.742 6.19873 15.0384 7.03848 15.0384H10.8077V16.5384ZM8.25003 12.7499V11.25H15.75V12.7499H8.25003ZM13.1923 16.5384V15.0384H16.9615C17.8013 15.0384 18.5176 14.742 19.1106 14.149C19.7035 13.5561 20 12.8397 20 12C20 11.1602 19.7035 10.4439 19.1106 9.85092C18.5176 9.25797 17.8013 8.9615 16.9615 8.9615H13.1923V7.46155H16.9615C18.2172 7.46155 19.2875 7.90399 20.1725 8.78887C21.0575 9.67376 21.5 10.7439 21.5 11.9994C21.5 13.2549 21.0575 14.3253 20.1725 15.2105C19.2875 16.0958 18.2172 16.5384 16.9615 16.5384H13.1923Z" fill="currentColor"/>
</svg>`;

// const settingsIcon = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none">
//   <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="1.8"/>
//   <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
// </svg>`;

// const analysisIcon = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none">
//   <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
// </svg>`;

// const logoutIcon = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none">
//   <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
//   <polyline points="16 17 21 12 16 7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
//   <line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
// </svg>`;

// const settingsIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
//   <path d="M14.3712 11.9169C14.7527 11.3729 15.0588 10.7001 15.2685 9.94418C15.4782 9.18823 15.5868 8.36682 15.5868 7.53567C15.5868 5.97389 15.2101 4.47608 14.5396 3.37173C13.8691 2.26739 12.9597 1.64697 12.0115 1.64697C11.0633 1.64697 10.1539 2.26739 9.48337 3.37173C8.81288 4.47608 8.43619 5.97389 8.43619 7.53567C8.43619 8.36682 8.54474 9.18823 8.75448 9.94418C8.96423 10.7001 9.27025 11.3729 9.65179 11.9169C8.65081 12.6634 7.80155 13.869 7.20556 15.3895C6.60957 16.91 6.29207 18.681 6.29102 20.4908C6.29102 20.8032 6.36635 21.1027 6.50045 21.3236C6.63455 21.5445 6.81643 21.6686 7.00607 21.6686C7.19572 21.6686 7.3776 21.5445 7.5117 21.3236C7.6458 21.1027 7.72113 20.8032 7.72113 20.4908C7.72113 18.6167 8.17315 16.8193 8.97775 15.4941C9.78235 14.1689 10.8736 13.4244 12.0115 13.4244C13.1494 13.4244 14.2406 14.1689 15.0452 15.4941C15.8498 16.8193 16.3018 18.6167 16.3018 20.4908C16.3018 20.8032 16.3772 21.1027 16.5113 21.3236C16.6454 21.5445 16.8273 21.6686 17.0169 21.6686C17.2065 21.6686 17.3884 21.5445 17.5225 21.3236C17.6566 21.1027 17.732 20.8032 17.732 20.4908C17.7309 18.681 17.4134 16.91 16.8174 15.3895C16.2214 13.869 15.3722 12.6634 14.3712 11.9169ZM12.0115 11.0689C11.5872 11.0689 11.1725 10.8617 10.8197 10.4734C10.4669 10.0852 10.192 9.53339 10.0296 8.88778C9.86724 8.24217 9.82476 7.53175 9.90753 6.84638C9.9903 6.161 10.1946 5.53144 10.4946 5.03731C10.7946 4.54318 11.1769 4.20667 11.593 4.07034C12.0091 3.93401 12.4404 4.00398 12.8324 4.2714C13.2244 4.53883 13.5594 4.99169 13.7951 5.57272C14.0309 6.15376 14.1567 6.83687 14.1567 7.53567C14.1567 8.47274 13.9307 9.37143 13.5284 10.034C13.1261 10.6966 12.5804 11.0689 12.0115 11.0689Z" fill="#ADDEE0"/>
//   <path d="M5.02468 11.4458C4.56707 10.597 4.26814 9.5485 4.16389 8.42645C4.05964 7.30439 4.15451 6.15663 4.43707 5.12131C4.71963 4.08599 5.17785 3.20724 5.75657 2.59084C6.33528 1.97444 7.00982 1.64667 7.699 1.64697C7.88865 1.64697 8.07053 1.77106 8.20463 1.99192C8.33873 2.21279 8.41406 2.51236 8.41406 2.82471C8.41406 3.13707 8.33873 3.43663 8.20463 3.6575C8.07053 3.87837 7.88865 4.00245 7.699 4.00245C7.13007 4.00245 6.58443 4.3747 6.18213 5.03731C5.77983 5.69992 5.55383 6.59861 5.55383 7.53567C5.55484 8.15427 5.65444 8.76159 5.84267 9.29689C6.03089 9.8322 6.30115 10.2767 6.62641 10.586C6.73243 10.6867 6.82098 10.8306 6.88358 11.0038C6.94618 11.177 6.98073 11.3738 6.98394 11.5753C6.98694 11.7753 6.95897 11.9732 6.90267 12.1504C6.84637 12.3276 6.7636 12.4782 6.66217 12.5882L6.38329 12.8944L6.29034 12.9768C5.42841 13.6502 4.70125 14.7152 4.19451 16.0464C3.68778 17.3776 3.42256 18.9196 3.4301 20.4908C3.4301 20.8032 3.35476 21.1027 3.22066 21.3236C3.08656 21.5445 2.90469 21.6686 2.71504 21.6686C2.5254 21.6686 2.34352 21.5445 2.20942 21.3236C2.07532 21.1027 1.99998 20.8032 1.99998 20.4908C1.99414 18.6835 2.26897 16.9037 2.79837 15.3206C3.32777 13.7375 4.09414 12.4036 5.02468 11.4458Z" fill="currentColor"/>
//   <path d="M18.9753 11.4458C19.4329 10.597 19.7319 9.5485 19.8361 8.42645C19.9404 7.30439 19.8455 6.15663 19.5629 5.12131C19.2804 4.08599 18.8221 3.20724 18.2434 2.59084C17.6647 1.97444 16.9902 1.64667 16.301 1.64697C16.1114 1.64697 15.9295 1.77106 15.7954 1.99192C15.6613 2.21279 15.5859 2.51236 15.5859 2.82471C15.5859 3.13707 15.6613 3.43663 15.7954 3.6575C15.9295 3.87837 16.1114 4.00245 16.301 4.00245C16.8699 4.00245 17.4156 4.3747 17.8179 5.03731C18.2202 5.69992 18.4462 6.59861 18.4462 7.53567C18.4452 8.15427 18.3456 8.76159 18.1573 9.29689C17.9691 9.8322 17.6988 10.2767 17.3736 10.586C17.2676 10.6867 17.179 10.8306 17.1164 11.0038C17.0538 11.177 17.0193 11.3738 17.0161 11.5753C17.0131 11.7753 17.041 11.9732 17.0973 12.1504C17.1536 12.3276 17.2364 12.4782 17.3378 12.5882L17.6167 12.8944L17.7097 12.9768C18.5716 13.6502 19.2987 14.7152 19.8055 16.0464C20.3122 17.3776 20.5774 18.9196 20.5699 20.4908C20.5699 20.8032 20.6452 21.1027 20.7793 21.3236C20.9134 21.5445 21.0953 21.6686 21.285 21.6686C21.4746 21.6686 21.6565 21.5445 21.7906 21.3236C21.9247 21.1027 22 20.8032 22 20.4908C22.0059 18.6835 21.731 16.9037 21.2016 15.3206C20.6722 13.7375 19.9059 12.4036 18.9753 11.4458Z" fill="currentColor"/>
// </svg>`;

/** Inline SVG for the Profile/Settings nav item icon. */
const settingsIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
  <circle cx="12" cy="8" r="4" stroke="currentColor" stroke-width="1.8"/>
  <path d="M4 20c0-4 3.582-7 8-7s8 3 8 7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
</svg>`;

/** Inline SVG for the (currently unused/commented-out) Analysis nav item icon. */
const analysisIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
  <path d="M21.7099 7.28994C21.617 7.19621 21.5064 7.12182 21.3845 7.07105C21.2627 7.02028 21.132 6.99414 20.9999 6.99414C20.8679 6.99414 20.7372 7.02028 20.6154 7.07105C20.4935 7.12182 20.3829 7.19621 20.2899 7.28994L13.9999 13.5899L9.70994 9.28994C9.61698 9.19621 9.50637 9.12182 9.38452 9.07105C9.26266 9.02028 9.13195 8.99414 8.99994 8.99414C8.86793 8.99414 8.73722 9.02028 8.61536 9.07105C8.4935 9.12182 8.3829 9.19621 8.28994 9.28994L2.28994 15.2899C2.19621 15.3829 2.12182 15.4935 2.07105 15.6154C2.02028 15.7372 1.99414 15.8679 1.99414 15.9999C1.99414 16.132 2.02028 16.2627 2.07105 16.3845C2.12182 16.5064 2.19621 16.617 2.28994 16.7099C2.3829 16.8037 2.4935 16.8781 2.61536 16.9288C2.73722 16.9796 2.86793 17.0057 2.99994 17.0057C3.13195 17.0057 3.26266 16.9796 3.38452 16.9288C3.50637 16.8781 3.61698 16.8037 3.70994 16.7099L8.99994 11.4099L13.2899 15.7099C13.3829 15.8037 13.4935 15.8781 13.6154 15.9288C13.7372 15.9796 13.8679 16.0057 13.9999 16.0057C14.132 16.0057 14.2627 15.9796 14.3845 15.9288C14.5064 15.8781 14.617 15.8037 14.7099 15.7099L21.7099 8.70994C21.8037 8.61698 21.8781 8.50638 21.9288 8.38452C21.9796 8.26266 22.0057 8.13195 22.0057 7.99994C22.0057 7.86793 21.9796 7.73722 21.9288 7.61536C21.8781 7.4935 21.8037 7.3829 21.7099 7.28994Z" fill="#ADDEE0"/>
  <path d="M13.4444 14.1685C13.6089 14.0586 13.8022 14 14 14C14.2652 14 14.5196 14.1054 14.7071 14.2929C14.8946 14.4804 15 14.7348 15 15C15 15.1978 14.9414 15.3911 14.8315 15.5556C14.7216 15.72 14.5654 15.8482 14.3827 15.9239C14.2 15.9996 13.9989 16.0194 13.8049 15.9808C13.6109 15.9422 13.4327 15.847 13.2929 15.7071C13.153 15.5673 13.0578 15.3891 13.0192 15.1951C12.9806 15.0011 13.0004 14.8 13.0761 14.6173C13.1518 14.4346 13.28 14.2784 13.4444 14.1685Z" fill="currentColor"/>
  <path d="M20.4444 7.16853C20.6089 7.05865 20.8022 7 21 7C21.2652 7 21.5196 7.10536 21.7071 7.29289C21.8946 7.48043 22 7.73478 22 8C22 8.19778 21.9414 8.39112 21.8315 8.55557C21.7216 8.72002 21.5654 8.84819 21.3827 8.92388C21.2 8.99957 20.9989 9.01937 20.8049 8.98079C20.6109 8.9422 20.4327 8.84696 20.2929 8.70711C20.153 8.56725 20.0578 8.38907 20.0192 8.19509C19.9806 8.00111 20.0004 7.80004 20.0761 7.61732C20.1518 7.43459 20.28 7.27841 20.4444 7.16853Z" fill="currentColor"/>
  <path d="M8.44443 9.16853C8.60888 9.05865 8.80222 9 9 9C9.26522 9 9.51957 9.10536 9.70711 9.29289C9.89464 9.48043 10 9.73478 10 10C10 10.1978 9.94135 10.3911 9.83147 10.5556C9.72159 10.72 9.56541 10.8482 9.38268 10.9239C9.19996 10.9996 8.99889 11.0194 8.80491 10.9808C8.61093 10.9422 8.43275 10.847 8.29289 10.7071C8.15304 10.5673 8.0578 10.3891 8.01922 10.1951C7.98063 10.0011 8.00043 9.80004 8.07612 9.61732C8.15181 9.43459 8.27998 9.27841 8.44443 9.16853Z" fill="currentColor"/>
  <path d="M2.44443 15.1685C2.60888 15.0586 2.80222 15 3 15C3.26522 15 3.51957 15.1054 3.70711 15.2929C3.89464 15.4804 4 15.7348 4 16C4 16.1978 3.94135 16.3911 3.83147 16.5556C3.72159 16.72 3.56541 16.8482 3.38268 16.9239C3.19996 16.9996 2.99889 17.0194 2.80491 16.9808C2.61093 16.9422 2.43275 16.847 2.29289 16.7071C2.15304 16.5673 2.0578 16.3891 2.01922 16.1951C1.98063 16.0011 2.00043 15.8 2.07612 15.6173C2.15181 15.4346 2.27998 15.2784 2.44443 15.1685Z" fill="currentColor"/>
</svg>`;

/** Inline SVG for the Logout nav item icon. */
const logoutIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
  <path d="M6 2H15C15.5304 2 16.0391 2.21071 16.4142 2.58579C16.7893 2.96086 17 3.46957 17 4V5C17 5.26522 16.8946 5.51957 16.7071 5.70711C16.5196 5.89464 16.2652 6 16 6C15.7348 6 15.4804 5.89464 15.2929 5.70711C15.1054 5.51957 15 5.26522 15 5V4H6V20H15V19C15 18.7348 15.1054 18.4804 15.2929 18.2929C15.4804 18.1054 15.7348 18 16 18C16.2652 18 16.5196 18.1054 16.7071 18.2929C16.8946 18.4804 17 18.7348 17 19V20C17 20.5304 16.7893 21.0391 16.4142 21.4142C16.0391 21.7893 15.5304 22 15 22H6C5.46957 22 4.96086 21.7893 4.58579 21.4142C4.21071 21.0391 4 20.5304 4 20V4C4 3.46957 4.21071 2.96086 4.58579 2.58579C4.96086 2.21071 5.46957 2 6 2Z" fill="currentColor"/>
  <path d="M16.795 16.2949C17.185 16.6849 17.815 16.6849 18.205 16.2949L21.793 12.7069C21.9805 12.5194 22.0858 12.265 22.0858 11.9999C22.0858 11.7347 21.9805 11.4804 21.793 11.2929L18.205 7.70488C18.0149 7.53307 17.7661 7.44087 17.5099 7.44734C17.2538 7.45381 17.0099 7.55844 16.8287 7.73962C16.6476 7.9208 16.5429 8.16467 16.5365 8.42081C16.53 8.67696 16.6222 8.92579 16.794 9.11588L18.67 10.9999H10C9.73478 10.9999 9.48043 11.1052 9.29289 11.2928C9.10536 11.4803 9 11.7347 9 11.9999C9 12.2651 9.10536 12.5195 9.29289 12.707C9.48043 12.8945 9.73478 12.9999 10 12.9999H18.67L16.794 14.8839C16.6073 15.0713 16.5026 15.3251 16.5028 15.5896C16.503 15.8541 16.6081 16.1077 16.795 16.2949Z" fill="currentColor"/>
</svg>`;
 
/** Inline SVG for the Invite (referrals) nav item icon. */
const inviteesIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
  <g clip-path="url(#clip0_2375_10998)">
  <path d="M14 14.252V16.342C13.0949 16.022 12.1263 15.9239 11.1754 16.0558C10.2245 16.1877 9.3192 16.5459 8.53543 17.1002C7.75166 17.6545 7.11234 18.3888 6.67116 19.2414C6.22998 20.094 5.99982 21.04 6 22L4 21.999C3.99969 20.7779 4.27892 19.5729 4.8163 18.4764C5.35368 17.3799 6.13494 16.4209 7.10022 15.673C8.0655 14.9251 9.18918 14.4081 10.3852 14.1616C11.5811 13.9152 12.8177 13.9457 14 14.251V14.252ZM12 13C8.685 13 6 10.315 6 7C6 3.685 8.685 1 12 1C15.315 1 18 3.685 18 7C18 10.315 15.315 13 12 13ZM12 11C14.21 11 16 9.21 16 7C16 4.79 14.21 3 12 3C9.79 3 8 4.79 8 7C8 9.21 9.79 11 12 11ZM18 17V14H20V17H23V19H20V22H18V19H15V17H18Z" fill="currentColor"/>
  </g>
  <defs>
  <clipPath id="clip0_2375_10998">
  <rect width="24" height="24" fill="currentColor"/>
  </clipPath>
  </defs>
</svg>`;

/**
 * Builds the list of sidebar nav entries. Each entry has: id, label, icon (SVG markup),
 * `to` (route path, or null for action-based items), `matchPaths` (paths that mark it
 * active), optional `badgeCount`, and optional `action` (invoked instead of routing —
 * used by Profile, which needs to resolve the current user first, and by Logout).
 */
const navItems = computed(() => [
  {
    id: 'home',
    label: 'Community',
    icon: communityIcon,
    to: '/',
    matchPaths: ['/', '/post'],
    badgeCount: 0,
  },
  {
    id: 'message',
    label: 'Messages',
    icon: messagesIcon,
    to: '/messages',
    matchPaths: ['/messages'],
    badgeCount: unreadMessagesCount.value,
  },
  {
    id: 'groups',
    label: 'Groups',
    icon: groupsIcon,
    to: '/groups',
    matchPaths: ['/groups', '/group/'],
    badgeCount: 0,
  },
  {
    id: 'quicklinks',
    label: 'Quick Links',
    icon: quickLinksIcon,
    to: '/about',
    matchPaths: ['/about'],
    badgeCount: 0,
  },
  {
    id: 'settings',
    label: 'Profile',
    icon: settingsIcon,
    // OLD: used router-link with to computed from userdata — when userdata isn't loaded yet
    // the fallback was '/', causing a NavigationDuplicated error if user was already on '/'
    // which made the Profile link appear unresponsive/unclickable intermittently.
    // OLD code:
    // to: userdata.value?.username
    //   ? `/user/${userdata.value.username}`
    //   : userdata.value?.id
    //     ? `/profile/${userdata.value.id}`
    //     : '/',
    // FIX: use action-based navigation so we always navigate imperatively.
    // If userdata is not loaded yet, dispatch getMe first then navigate.
    // This never falls back to '/' and never causes a NavigationDuplicated error.
    to: null,
    matchPaths: ['/user', '/profile'],
    action: () => {
      const u = userdata.value || store.getters.getUser;
      if (u?.username) router.push(`/user/${u.username}`);
      else if (u?.id) router.push(`/profile/${u.id}`);
      else store.dispatch('getMe').then(() => {
        const fresh = store.getters.getUser;
        if (fresh?.username) router.push(`/user/${fresh.username}`);
        else if (fresh?.id) router.push(`/profile/${fresh.id}`);
      });
    },
  },
  // {
  //   id: 'analysis',
  //   label: 'Analysis',
  //   icon: analysisIcon,
  //   to: null,
  //   matchPaths: [],
  // },
  {
    id: 'referrals',
    label: 'Invite',
    icon: inviteesIcon,
    to: '/referrals',
    matchPaths: ['/referrals'],
  },
  {
    id: 'logout',
    label: 'Log out',
    icon: logoutIcon,
    to: '',
    matchPaths: [],
    action: () => logOut(),
  },
].filter(item => item.id !== 'referrals' || showInviteCode.value));

// Responsive: collapse on smaller screens
/** Auto-collapses/expands the desktop sidebar based on viewport width, notifying the parent via `toggle`. */
const updateCollapse = () => {
  if (window.innerWidth < 900) {
    isCollapsed.value = true;
    emit('toggle', true);
  } else {
    isCollapsed.value = false;
    emit('toggle', false);
  }
};

/** Applies the initial responsive collapse state and subscribes to window resize to keep it current. */
onMounted(() => {
  updateCollapse();
  window.addEventListener('resize', updateCollapse);
});

/** Cleans up the resize listener registered above. */
onUnmounted(() => {
  window.removeEventListener('resize', updateCollapse);
});
</script>

<style lang="scss" scoped>
.nav-sidebar__toggle {
  /* OLD: border-top: 1px solid #e5e7eb; */
  border-top: 1px solid var(--color-box-border);
  z-index: 1202;
  cursor: pointer !important;
  pointer-events: all !important;
}
.nav-sidebar {
  --sidebar-width: 240px;
  --sidebar-collapsed-width: 72px;
  --sidebar-bg: var(--color-app-background-gradient, linear-gradient(135deg, var(--color-background-primary) 0%, var(--color-background-secondary) 100%));
  // --color-sidebar-border: var(--color-header-border-light-v2, rgba(0,0,0,0.08));
  --sidebar-item-color: var(--color-foreground-primary);
  --sidebar-active-bg: var(--color-quick-link-icon-bg);
  --sidebar-active-color: var(--color-text-black-white);
  --sidebar-hover-bg: rgba(4, 154, 140, 0.08);
  --sidebar-transition: 0.28s cubic-bezier(0.4, 0, 0.2, 1);

  width: var(--sidebar-width);
  min-width: var(--sidebar-width);
  height: 100vh;
  // background: var(--sidebar-bg);
  background: var(--color-header-bg-light-v2);
  border-right: 1px solid var(--color-button-border);
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 0;
  left: 0;
  z-index: 1201;
  overflow: visible;
  transition: width var(--sidebar-transition), min-width var(--sidebar-transition);
  flex-shrink: 0;

  &--collapsed {
    width: var(--sidebar-collapsed-width);
    min-width: var(--sidebar-collapsed-width);

    .nav-sidebar__logo-full { display: none; }
    .nav-sidebar__logo-letter { display: flex; }
    .nav-item__label { opacity: 0; width: 0; overflow: hidden; margin: 0; }
    .nav-item__badge { right: 6px; }
    .nav-sidebar__toggle { margin-left: 0; }
  }

  &__brand {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 0px 25px 0px;
    min-height: 65px;
    // border-bottom: 1px solid var(--color-button-border);
    flex-shrink: 0;
  }

  &__logo-wrap {
    display: flex;
    align-items: center;
    cursor: pointer;
    flex: 1;
    min-width: 0;
    overflow: hidden;
  }

  &__logo-full {
    height: 34px;
    max-width: 130px;
    object-fit: contain;
    transition: opacity var(--sidebar-transition);
  }

  &__logo-letter {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    // background: var(--color-quick-link-icon-bg);
    /* OLD: color: #ffffff; */
    color: var(--color-white);
    font-size: 20px;
    font-weight: 800;
    flex-shrink: 0;
    letter-spacing: -1px;
  }

  &__toggle {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border: none;
    background: transparent;
    border-radius: 8px;
    cursor: pointer;
    // color: var(--sidebar-item-color);
    color: var(--color-text-black-white);
    // opacity: 0.65;
    flex-shrink: 0;
    transition: background 0.18s ease, opacity 0.18s ease;
    margin-left: 8px;

    &:hover {
      // background: var(--sidebar-hover-bg);
      // opacity: 1;
    }
  }

  &__nav {
    display: flex;
    flex-direction: column;
    gap: 18px;
    padding: 12px 10px;
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }
  }
}

.nav-sidebar__donate-wrap {
  padding: 0 10px 6px;
  flex-shrink: 0;
}

.nav-sidebar__donate-btn {
  display: flex;
  align-items: center;
  justify-content: start;
  gap: 10px;
  width: 100%;
  padding: 10px 14px;
  border: none;
  border-radius: 6px;
  background-image: linear-gradient(to bottom, #f2706b, #ef3c36);
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.18s ease;
  border: 2px solid var(--color-button-border);
  position: relative;
  overflow: hidden;

  &:hover {
    opacity: 0.85;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: -150%;
    width: 60%;
    height: 100%;
    background: linear-gradient(
      120deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.45) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    transform: skewX(-20deg);
    // animation: nav-sidebar-donate-shimmer 2.6s ease-in-out infinite;
    pointer-events: none;
  }
}

// @keyframes nav-sidebar-donate-shimmer {
//   0% { left: -150%; }
//   60% { left: 150%; }
//   100% { left: 150%; }
// }

.nav-sidebar__donate-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.nav-sidebar--collapsed .nav-sidebar__donate-label {
  display: none;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-radius: 5.8px;
  color: var(--sidebar-item-color);
  text-decoration: none;
  cursor: pointer;
  transition: background 0.18s ease, color 0.18s ease;
  position: relative;
  white-space: nowrap;
  opacity: 0.7;
  user-select: none;

  &:hover {
    background: var(--sidebar-hover-bg);
    opacity: 1;
    color: var(--color-quick-link-icon-bg);
  }

  &--active {
    background: var(--color-quick-link-icon-bg);
    color: var(--color-text-black-white);
    opacity: 1;
    border: 1px solid var(--color-button-border);
    height: 40px;

    &:hover {
      background: var(--color-quick-link-icon-bg);
      color: var(--color-text-black-white);
    }

    .nav-item__icon :deep(svg path),
    .nav-item__icon :deep(svg circle),
    .nav-item__icon :deep(svg line),
    .nav-item__icon :deep(svg polyline),
    .nav-item__icon :deep(svg rect) {
      // stroke: #ffffff;
      stroke: var(--color-text-black-white);
      fill: transparent;
    }

    // Ring-style badge: a solid fill would be the same accent color as the
    // active pill itself (both derive from --color-accent-primary) and vanish
    // against it, so the active state swaps to a transparent center with a
    // contrast-colored ring instead — same --color-text-black-white token
    // the icon override above uses, so it flips light/dark correctly too.
    .nav-item__badge {
      background: transparent;
      border: 1.5px solid var(--color-text-black-white);
      color: var(--color-text-black-white);
    }
  }

  &--logout {
    margin-top: auto;
    /* OLD: color: #E73C3E; */
    color: var(--color-error-text);
    opacity: 0.8;

    &:hover {
      background: rgba(229, 62, 62, 0.08);
      /* OLD: color: #E73C3E; */
      color: var(--color-error-text);
      opacity: 1;
    }
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    flex-shrink: 0;
  }

  &__label {
    font-size: 14px;
    font-weight: 500;
    transition: opacity 0.22s ease, width 0.22s ease;
    flex: 1;
    min-width: 0;
  }

  &__badge {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    /* OLD: background: #E73C3E; color: #fff; (fixed red, didn't adapt per theme) */
    // Same accent + contrast-text pair the active icon/badge overrides use
    // above, so this flips correctly across light/dark and every brand preset.
    background: var(--color-accent-primary);
    color: var(--color-text-black-white);
    font-size: 10px;
    font-weight: 700;
    border-radius: 999px;
    min-width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 4px;
  }
}

// Logout item at bottom
.nav-sidebar__nav .nav-item:last-child {
  // margin-top: auto;
}

// Mobile: hidden by default, slides in as a drawer when mobileOpen prop is true
@media (max-width: 639px) {
  .nav-sidebar {
    position: fixed;
    top: 0;
    left: 0;
    height: 100%;
    width: 80%;
    max-width: 300px;
    z-index: 1300;
    transform: translateX(-100%);
    transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
    overflow-y: auto;
    // Force full labels visible in drawer — never collapsed on mobile
    .nav-item__label {
      opacity: 1 !important;
      width: auto !important;
      overflow: visible !important;
      margin: 0 !important;
    }
    // Hide the desktop collapse toggle inside the drawer
    .nav-sidebar__toggle {
      display: none;
    }
    // Larger logo on mobile
    .nav-sidebar__logo-full {
      height: 50px !important;
      max-width: 170px !important;
    }
    // Add horizontal padding so logo has breathing room
    .nav-sidebar__brand {
      padding-left: 16px;
    }
  }

  .nav-sidebar--mobile-open {
    transform: translateX(0);
  }
}

// Close button — v-show controls visibility; this just defines layout when visible
.nav-sidebar__mobile-close {
  display: flex;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--color-post-button-bg, rgba(0,0,0,0.06));
  border: none;
  cursor: pointer;
  color: var(--color-foreground-primary-v2);
  margin-right: 8px;
  &:hover { background: var(--color-background-btn-secondary); }
}

// Backdrop shown behind the drawer on mobile
.nav-sidebar-backdrop {
  display: none;
  @media (max-width: 639px) {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1299;
  }
}
.arrow-box  {
  // background: var(--color-app-card-bg);
 background: var(--color-button-border);
  border: 1px solid var(--color-header-border-light-v2);;
  border-radius: 6px;
  width: 30px;
}
.nav-sidebar {
  overflow: visible;
}
.nav-item--disabled {
  opacity: 0.4;
  cursor: no-drop;
  // pointer-events: none;
}

.nav-sidebar__version {
  flex-shrink: 0;
  padding: 10px 14px 10px 27px;
  text-align: left;
  font-size: 14px;
  font-weight: 500;
  color: var(--sidebar-item-color);
  opacity: 0.5;
  border-top: 1px solid var(--color-box-border);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.nav-sidebar--collapsed .nav-sidebar__version {
  padding: 0px;
  text-align: center;
}
@media (max-width: 639px) {
  .nav-sidebar--collapsed.nav-sidebar--mobile-open .nav-sidebar__version {
    padding: 10px 14px 10px 27px;
    text-align: left;
  }
}
</style>
